import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import { MeetingIcon, PenIcon, TravelTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import UserRoleCard from '@repo/ui/user-role-card'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { API } from '../../../api/apis.config'
import { getCenter } from '../../../api/centers/get-center'
import { updateAssignedJourneys } from '../../../api/centers/update-assigned-journeys'
import { getStaffRequests } from '../../../api/staff-requests/get-staff-requests'
import DynamicDataTable from '../../../components/elements/dynamic-table'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import { QualificationBadge } from '../../../components/elements/qualification-badge'
import CenterJourneySelectionList from '../../../components/pages/centers/assign-journey-to-center/center-journey-selection-list'
import CenterMainStaffBadgeInfo from '../../../components/pages/centers/center-main-staff-badge-info'
import { Paths } from '../../../lib/constants/path'
import { useObserver } from '../../../lib/contexts/observer'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { AffectedStaffStatusEnum } from '../../../lib/types/enums/affected-staff/affected-staff-status.enum'
import { missionStatusEnumMapping } from '../../../lib/types/enums/mission/mission-status.enum'
import { staffQualificationEnumMapping } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaff } from '../../../lib/types/models/affectedStaff/affected-staff'
import { AffectedStaffFilters } from '../../../lib/types/models/affectedStaff/affected-staff-input'
import { AssignJourneyToCenterRequestInput } from '../../../lib/types/models/center/center-input'
import { CenterProperties } from '../../../lib/types/models/center/properties.center'
import { StaffProperties } from '../../../lib/types/models/staff/properties.staff'
import { StaffRequestProperties } from '../../../lib/types/models/staff-request/properties.staff-request'
import { StaffRequest } from '../../../lib/types/models/staff-request/staff-request'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { IdInput } from '../../../lib/types/utils'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'

type Props = {
  id: number
}

export function CenterStaffs({ id }: Props) {
  const { t } = useTranslate()
  const { modalState, toggleModal } = useModalManager(['update-center-staff-requests'])
  const { navigate } = useNavigator()
  const [step, setStep] = useState(2)
  const centerStaffObserver = useObserver({ key: 'center-staffs' })

  const centerQuery = useQuery({
    queryKey: ['center', id],
    queryFn: () => {
      if (!id) return
      return getCenter(id)
    },
    enabled: !!id
  })

  const { data: staffRequests, refetch: refetchStaffRequests } = useQuery({
    queryKey: ['staff-requests', 'centerId', id],
    queryFn: () => {
      if (!id) return
      return getStaffRequests({ filter: { centerIds: [id] } })
    },
    enabled: !!id
  })

  const assignJourneysMutation = useMutation({
    mutationFn: updateAssignedJourneys,
    onError: showAxiosError,
    onSuccess: () => {
      setStep(2)
      centerQuery.refetch()
      centerStaffObserver.notify(['center-tefl-costs-table'])
      refetchStaffRequests()
    }
  })

  const coursesStaffRequests = StaffRequestProperties.getCoursesStaffRequests(
    staffRequests?.data ?? []
  )
  const activitiesStaffRequests = StaffRequestProperties.getActivitiesStaffRequests(
    staffRequests?.data ?? []
  )

  const staffRequestColumns: Column<StaffRequest>[] = [
    {
      accessorKey: 'qualification',
      header: 'Qualification',
      cell: ({ row }) => {
        const { type, label } = staffQualificationEnumMapping[row.original.qualification]

        return (
          <Badge
            variant="white"
            textColor={
              type === 'french' ? tailwindColors.fushia.DEFAULT : tailwindColors.purple.DEFAULT
            }
            text={t(label)}
          />
        )
      }
    },
    {
      header: '#',
      accessorKey: 'numberOfStaff'
    },
    {
      header: 'Statut',
      accessorKey: 'status',
      cell: ({ row }) => (
        <Badge
          variant={missionStatusEnumMapping[row.original.status].variant}
          text={missionStatusEnumMapping[row.original.status].label}
        />
      )
    }
  ]

  const affectedStaffColumns: Column<AffectedStaff>[] = [
    {
      accessorKey: 'staff.lastName',
      enableSorting: true,
      header: 'NOM',
      cell: ({ row }) => {
        const staff = row.original.staff
        return !staff ? (
          '-'
        ) : (
          <Typography.Link
            variant="body"
            color={tailwindColors.blue.DEFAULT}
            onClick={() =>
              navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', row.original.staff.id.toString()))
            }
          >
            {staff.lastName.toUpperCase()}
          </Typography.Link>
        )
      }
    },
    {
      accessorKey: 'staff.firstName',
      enableSorting: true,
      header: 'Prénom',
      cell: ({ row }) =>
        valueRender(row.original.staff, (s) => StringFormatter.capitalize(s.firstName))
    },
    {
      accessorKey: 'staffRequest.qualification',
      header: 'Qualification',
      enableSorting: true,
      cell: ({ row }) => (
        <QualificationBadge qualification={row.original.staffRequest.qualification} />
      )
    },
    {
      accessorKey: 'staff.firstInvolvementAt',
      header: 'Ancienneté (en séjours)',
      enableSorting: true,
      cell: ({ row }) =>
        valueRender(row.original.staff, (s) => StaffProperties.getSeniority(s).toString())
    },
    {
      accessorKey: 'staff.phoneNumber',
      header: 'N° personnel',
      cell: ({ row }) => valueRender(row.original.staff, (s) => formatPhoneNumber(s.phoneNumber))
    },
    {
      accessorKey: 'staff.user.email',
      header: 'Email',
      enableSorting: true
    },
    {
      accessorKey: 'tripId',
      header: 'Voyage affecté',
      enableSorting: true,
      cell: ({ row }) => {
        const trip = row.original.trip
        if (!trip) return '-'
        return TripProperties.getTripLabel(trip)
      }
    },
    {
      accessorKey: 'tripBookingDiffused',
      header: 'Diffusion voyage',
      cell: ({ row }) => {
        const isTripBookingDiffused = row.original.travelChoiceDiffused
        return (
          <Badge
            variant={isTripBookingDiffused ? 'success' : 'grey'}
            text={isTripBookingDiffused ? 'Diffusé' : 'Non diffusé'}
          />
        )
      }
    },
    {
      accessorKey: 'hostFamilyId',
      header: 'Famille-hôtesse',
      enableSorting: true,
      cell: ({ row }) => {
        const hostFamily = row.original.hostFamily
        return (
          <Badge
            variant={hostFamily ? 'blue' : 'danger'}
            text={hostFamily ? 'Assigné' : 'Non assigné'}
          />
        )
      }
    }
  ]

  if (!id) return null
  if (!centerQuery.data) return <LoadingScreen />

  return (
    <div className="flex flex-col gap-5">
      <div />

      <CenterMainStaffBadgeInfo id={id} />

      <InnerBox className="rounded-2xl p-5 flex flex-col gap-4">
        <UserRoleCard
          label="Réunion avant départ"
          value={CenterProperties.getRadNameForRequestStaff(centerQuery.data.rad)}
          variant="primaryLight"
          icon={MeetingIcon}
        />

        <InnerBox className="rounded-lg p-5 flex flex-col gap-2 bg-white-hover">
          <div className="flex flex-row items-center gap-2 justify-between">
            <div className="flex flex-row items-center gap-2">
              <Typography variant="sub-heading">Demandes d'encadrants</Typography>
              <Badge variant="warning" text={`Total : ${staffRequests?.total ?? 0}`} />
            </div>

            <ActionButton
              variant="grey"
              icon={PenIcon}
              onClick={() => toggleModal('update-center-staff-requests')}
            />
          </div>

          <div className="flex flex-row gap-2">
            <div className="flex flex-col gap-3 w-full">
              <Typography variant="body" color={tailwindColors.primary.DEFAULT}>
                Cours
              </Typography>

              <DataTable
                bodyVariant="white"
                headerClassName="bg-white-active"
                tableClassName="border-grey-light"
                columns={staffRequestColumns}
                data={coursesStaffRequests}
                isLoading={false}
                rowCount={coursesStaffRequests.length}
                paginated={false}
              />
            </div>

            <div className="flex flex-col gap-2 w-full">
              <Typography variant="body" color={tailwindColors.primary.DEFAULT}>
                Activités
              </Typography>

              <DataTable
                bodyVariant="white"
                headerClassName="bg-white-active"
                tableClassName="border-grey-light"
                columns={staffRequestColumns}
                data={activitiesStaffRequests}
                isLoading={false}
                rowCount={activitiesStaffRequests.length}
                paginated={false}
              />
            </div>
          </div>
        </InnerBox>

        <DynamicDataTable<AffectedStaff, AffectedStaffFilters>
          className="bg-white-hover rounded-lg"
          columns={affectedStaffColumns}
          filters={{ centerId: centerQuery.data.id, statuses: [AffectedStaffStatusEnum.AFFECTED] }}
          endpoint={API.affectedStaffs.getAll.url}
          title="Encadrants"
          bodyVariant="white"
          version="FULL"
          permissions={{ delete: false }}
        />
      </InnerBox>

      <Modal
        isOpen={modalState['update-center-staff-requests']}
        title={CenterProperties.getModalTitleBasedOnStep(step)}
        titleAlignment="left"
        size={'medium'}
        hideActions
        icon={TravelTwoIcon}
        onCancel={() => toggleModal('update-center-staff-requests')}
      >
        <CenterJourneySelectionList
          step={step}
          onStepChange={(step) => {
            if (step < 2) toggleModal('update-center-staff-requests')
            else setStep(step)
          }}
          defaultValues={CenterProperties.mapCenterToInput(centerQuery.data).assignJourneyToCenter}
          onSubmit={async (values) => {
            const assignJourneyToCenterRequest =
              CenterProperties.mapAssignJourneyToCenterToRequestInput(values)
            const data: IdInput<AssignJourneyToCenterRequestInput> = {
              id: id,
              ...assignJourneyToCenterRequest
            }
            await assignJourneysMutation.mutateAsync(data)
            toggleModal('update-center-staff-requests')
          }}
        />
      </Modal>
    </div>
  )
}
